'use client';

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import {
  mseHandshakeMessage,
  parseWebrtcSignal,
  streamMseUrl,
  streamWsUrl,
  webrtcCandidateMessage,
  webrtcOfferMessage,
} from './url.js';

/**
 * MSE-first WebRTC-fallback client for go2rtc.
 *
 * Why two transports:
 *   1. MSE-LD ships H.265 fMP4 fragments straight from the relay to
 *      the browser's <video>. The browser hardware-decodes via the
 *      platform HEVC decoder (Apple Silicon, modern Intel iGPU, Pi 5
 *      VideoCore VII, modern Android SoCs). Server CPU per stream is
 *      bounded by packet repacketization — no decode/encode.
 *   2. WebRTC is the fallback for clients without a working HEVC
 *      decoder (Firefox, ancient hardware, Main10 mismatches that pass
 *      `MediaSource.isTypeSupported` but render black). go2rtc spawns
 *      `ffmpeg:<name>#video=h264` lazily for these consumers — only the
 *      fallback path pays the transcode cost.
 *
 * Failure detection for the MSE phase covers three cases:
 *   - WebSocket close before first segment → fall back.
 *   - `<video>` or `SourceBuffer` `error` event → fall back.
 *   - Watchdog: `video.currentTime` not advancing within 3 s of the
 *     first appended segment → fall back. This catches the "hvc1
 *     reported supported, decode actually fails silently" case (10-bit
 *     Main10 on Chromium, B-frame profile mismatches).
 *
 * Latency: MSE-LD drifts to 1.5-2 s without explicit live-edge
 * clamping. The `progress`-event handler jumps `currentTime` to within
 * 100 ms of `buffered.end(0)` whenever the gap exceeds 500 ms, which
 * keeps end-to-end LAN latency under ~600 ms in practice.
 */

interface Props {
  streamName: string;
  className?: string;
  /** Inline style for the wrapper `<div>`. The board kiosk uses this
   *  to set its picture-in-picture box size in `vw` units. */
  style?: CSSProperties;
  muted?: boolean;
  videoRef?: RefObject<HTMLVideoElement | null>;
  /** Override the relay base path. Defaults to `/streams`. */
  streamsBase?: string;
  /**
   * Override the inner `<video>` className. Defaults to `block w-full`,
   * which fits a flexible parent. The board kiosk swaps this to
   * `absolute` for picture-in-picture crop.
   */
  videoClassName?: string;
  /** Inline style for the inner `<video>` element. Used by the board
   *  for px-precise crop offset + scale. */
  videoStyle?: CSSProperties;
  /** Hide the "Verbinde mit Kamera…" / "Offline" overlay. The board
   *  kiosk wants a clean black square while connecting. */
  hideStateOverlay?: boolean;
  /** Optional layer rendered on top of the inner `<video>`, inside the
   *  wrapper. Used by the kiosk board to draw CV-detected hit markers
   *  above the live image. The caller is responsible for positioning
   *  (typically `absolute inset-0`). */
  overlay?: ReactNode;
}

const MSE_HANDSHAKE_TIMEOUT_MS = 3000;
const MSE_WATCHDOG_MS = 3000;
const LIVE_EDGE_GAP_S = 0.5;
const LIVE_EDGE_TARGET_S = 0.1;
/** After this many failed attempts the overlay says "offline"; retrying goes on. */
const RECONNECT_MAX_ATTEMPTS = 5;
const RECONNECT_BASE_DELAY_MS = 1500;
const RECONNECT_MAX_DELAY_MS = 30_000;
/** Live MSE playback that hasn't advanced for this long → reconnect. */
const MSE_STALL_MS = 9000;
/** A WebRTC peer that stays `disconnected` this long → reconnect. */
const WEBRTC_DISCONNECT_GRACE_MS = 5000;
/** WebRTC attempt with no picture after this long → drop and retry (a
 *  signalled peer whose media never connects would otherwise hang). */
const WEBRTC_CONNECT_TIMEOUT_MS = 15_000;

type State = 'connecting' | 'live' | 'offline';

export function Go2rtcVideo({
  streamName,
  className,
  style,
  muted = true,
  videoRef,
  streamsBase,
  videoClassName,
  videoStyle,
  hideStateOverlay = false,
  overlay,
}: Props) {
  const internalRef = useRef<HTMLVideoElement | null>(null);
  const ref = videoRef ?? internalRef;
  const [state, setState] = useState<State>('connecting');

  useEffect(() => {
    const video = ref.current;
    if (!video || !streamName) return;

    let cancelled = false;
    let attempt = 0;
    let teardown: (() => void) | null = null;
    let retryTimer: number | null = null;

    const scheduleReconnect = () => {
      if (cancelled) return;
      attempt += 1;
      // Show "offline" after a few failures but keep trying (capped
      // backoff), so the picture comes back by itself with the camera.
      if (attempt >= RECONNECT_MAX_ATTEMPTS) setState('offline');
      const delay = Math.min(
        RECONNECT_BASE_DELAY_MS * 2 ** Math.min(attempt - 1, 10),
        RECONNECT_MAX_DELAY_MS,
      );
      retryTimer = window.setTimeout(() => {
        retryTimer = null;
        connect();
      }, delay);
    };

    const onLive = () => {
      if (cancelled) return;
      setState('live');
      attempt = 0;
    };

    // One full attempt = try MSE, on failure drop and try WebRTC. If
    // WebRTC also fails (or disconnects later), schedule a backoff retry
    // that starts the next attempt back at MSE.
    const connect = () => {
      if (cancelled) return;
      teardown?.();
      // Keep "offline" visible during the slow background retries.
      if (attempt < RECONNECT_MAX_ATTEMPTS) setState('connecting');

      const stop = startMse({
        video,
        streamName,
        streamsBase,
        onLive,
        onFailed: () => {
          if (cancelled) return;
          // MSE didn't work; try WebRTC as the fallback for *this*
          // attempt without touching the backoff counter.
          teardown = startWebrtc({
            video,
            streamName,
            streamsBase,
            onLive,
            onDisconnected: scheduleReconnect,
          });
        },
        onDisconnected: scheduleReconnect,
      });
      teardown = stop;
    };

    connect();

    return () => {
      cancelled = true;
      if (retryTimer != null) window.clearTimeout(retryTimer);
      teardown?.();
      video.srcObject = null;
      try {
        video.removeAttribute('src');
        video.load();
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamName, ref, streamsBase]);

  const defaultVideoClass = state === 'live' ? 'block w-full' : 'block w-full opacity-30';
  return (
    <div className={`relative ${className ?? ''}`} style={style}>
      <video
        ref={ref}
        className={videoClassName ?? defaultVideoClass}
        style={videoStyle}
        autoPlay
        playsInline
        muted={muted}
        controls={false}
      />
      {!hideStateOverlay && state !== 'live' && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-zinc-300">
          {state === 'connecting'
            ? 'Verbinde mit Kamera…'
            : 'Kamera offline. URL / Netzwerk prüfen.'}
        </div>
      )}
      {overlay}
    </div>
  );
}

interface TransportArgs {
  video: HTMLVideoElement;
  streamName: string;
  streamsBase: string | undefined;
  onLive: () => void;
  /** MSE-only: the transport reached "WS open" but couldn't deliver
   *  decodable media in time. Caller should drop and try WebRTC. */
  onFailed?: () => void;
  /** Either transport: was live, then WS closed / pc disconnected. */
  onDisconnected: () => void;
}

function startMse(args: TransportArgs): () => void {
  const { video, streamName, streamsBase, onLive, onFailed, onDisconnected } = args;
  let live = false;
  let cancelled = false;
  let ws: WebSocket | null = null;
  let mediaSource: MediaSource | null = null;
  let sourceBuffer: SourceBuffer | null = null;
  const queue: ArrayBuffer[] = [];
  let handshakeTimer: number | null = null;
  let watchdogTimer: number | null = null;
  let watchdogStartTime = 0;
  let lastProgressAt = 0;
  let progressHandler: (() => void) | null = null;
  let videoErrorHandler: (() => void) | null = null;
  let objectUrl: string | null = null;

  const fail = (reason: string) => {
    if (cancelled) return;
    cancelled = true;
    console.warn('[go2rtc/mse] failed', { reason });
    cleanup();
    if (live) onDisconnected();
    else onFailed?.();
  };

  const cleanup = () => {
    if (handshakeTimer != null) {
      window.clearTimeout(handshakeTimer);
      handshakeTimer = null;
    }
    if (watchdogTimer != null) {
      window.clearInterval(watchdogTimer);
      watchdogTimer = null;
    }
    if (progressHandler) {
      video.removeEventListener('progress', progressHandler);
      progressHandler = null;
    }
    if (videoErrorHandler) {
      video.removeEventListener('error', videoErrorHandler);
      videoErrorHandler = null;
    }
    try {
      ws?.close();
    } catch {}
    ws = null;
    try {
      if (mediaSource && mediaSource.readyState === 'open') mediaSource.endOfStream();
    } catch {}
    mediaSource = null;
    sourceBuffer = null;
    if (objectUrl) {
      try {
        URL.revokeObjectURL(objectUrl);
      } catch {}
      objectUrl = null;
    }
  };

  const flushQueue = () => {
    if (!sourceBuffer || sourceBuffer.updating || queue.length === 0) return;
    const next = queue.shift();
    if (!next) return;
    try {
      sourceBuffer.appendBuffer(next);
    } catch (err) {
      fail(`appendBuffer: ${(err as Error).message}`);
    }
  };

  const initMediaSource = (mime: string) => {
    if (typeof MediaSource === 'undefined') {
      fail('MediaSource not supported');
      return;
    }
    if (!MediaSource.isTypeSupported(mime)) {
      fail(`mime not supported: ${mime}`);
      return;
    }
    mediaSource = new MediaSource();
    objectUrl = URL.createObjectURL(mediaSource);
    video.src = objectUrl;
    mediaSource.addEventListener(
      'sourceopen',
      () => {
        if (!mediaSource) return;
        try {
          sourceBuffer = mediaSource.addSourceBuffer(mime);
          sourceBuffer.mode = 'segments';
          sourceBuffer.addEventListener('updateend', flushQueue);
          sourceBuffer.addEventListener('error', () => fail('sourceBuffer error'));
          flushQueue();
        } catch (err) {
          fail(`addSourceBuffer: ${(err as Error).message}`);
        }
      },
      { once: true },
    );
  };

  const armWatchdog = () => {
    if (watchdogTimer != null) return;
    watchdogStartTime = video.currentTime;
    const deadline = performance.now() + MSE_WATCHDOG_MS;
    watchdogTimer = window.setInterval(() => {
      if (live) {
        // Already passed liveness check on a previous tick — keep
        // the timer running as a stall detector: reconnect only after
        // MSE_STALL_MS without progress, not on a short hiccup.
        if (video.currentTime - watchdogStartTime > 0.1) {
          watchdogStartTime = video.currentTime;
          lastProgressAt = performance.now();
        } else if (performance.now() - lastProgressAt > MSE_STALL_MS) {
          fail('stall while live');
        }
        return;
      }
      if (video.currentTime - watchdogStartTime > 0.1) {
        watchdogStartTime = video.currentTime;
        lastProgressAt = performance.now();
        live = true;
        onLive();
        return;
      }
      if (performance.now() > deadline) {
        fail('watchdog: currentTime did not advance');
      }
    }, 500);
  };

  const armLatencyClamp = () => {
    progressHandler = () => {
      if (!video.buffered.length) return;
      const end = video.buffered.end(video.buffered.length - 1);
      if (end - video.currentTime > LIVE_EDGE_GAP_S) {
        video.currentTime = Math.max(end - LIVE_EDGE_TARGET_S, 0);
      }
    };
    video.addEventListener('progress', progressHandler);
  };

  videoErrorHandler = () => fail(`video.error: ${video.error?.message ?? 'unknown'}`);
  video.addEventListener('error', videoErrorHandler);

  ws = new WebSocket(streamMseUrl(streamName, streamsBase));
  ws.binaryType = 'arraybuffer';
  handshakeTimer = window.setTimeout(() => {
    if (!sourceBuffer) fail('handshake timeout');
  }, MSE_HANDSHAKE_TIMEOUT_MS);

  // go2rtc dispatches WS handlers by message type — the MSE handler
  // (`internal/mp4/ws.go`) only fires once the client sends a `{type:
  // "mse", value: "<codecs>"}` frame. Without it the server stays idle
  // and the handshake times out. Send immediately on open.
  ws.onopen = () => {
    if (cancelled) return;
    try {
      ws?.send(mseHandshakeMessage());
    } catch (err) {
      fail(`ws send: ${(err as Error).message}`);
    }
  };

  ws.onmessage = (ev) => {
    if (cancelled) return;
    if (typeof ev.data === 'string') {
      // go2rtc sends a single JSON handshake frame announcing the
      // codec/MIME, then switches to binary fMP4 fragments.
      try {
        const msg = JSON.parse(ev.data) as { type?: string; value?: string };
        if (msg.type === 'mse' && typeof msg.value === 'string') {
          initMediaSource(msg.value);
          armWatchdog();
          armLatencyClamp();
        } else if (msg.type === 'error') {
          fail(`relay error: ${msg.value ?? 'unknown'}`);
        }
      } catch (err) {
        fail(`bad handshake: ${(err as Error).message}`);
      }
      return;
    }
    // Binary fMP4 fragment.
    const buf = ev.data as ArrayBuffer;
    queue.push(buf);
    flushQueue();
  };

  ws.onerror = () => {
    // Browser already logs the failure; surface as a clean fail.
    if (!live) fail('ws error');
  };
  ws.onclose = () => {
    fail('ws closed');
  };

  return () => {
    cancelled = true;
    cleanup();
  };
}

function startWebrtc(args: TransportArgs): () => void {
  const { video, streamName, streamsBase, onLive, onDisconnected } = args;
  let cancelled = false;
  let live = false;
  let ws: WebSocket | null = null;
  let pc: RTCPeerConnection | null = null;
  let disconnectTimer: number | null = null;
  let connectTimer: number | null = null;
  // Our candidates wait for the offer; go2rtc's wait for its answer.
  let offerSent = false;
  let remoteSet = false;
  const pendingLocal: string[] = [];
  const pendingRemote: string[] = [];

  // Live = the <video> shows a frame. `track` fires while the answer is
  // applied, before any media arrives, so it can't count as live.
  const onFrame = () => {
    if (cancelled || live) return;
    live = true;
    if (connectTimer != null) {
      window.clearTimeout(connectTimer);
      connectTimer = null;
    }
    onLive();
  };
  video.addEventListener('loadeddata', onFrame);
  video.addEventListener('playing', onFrame);

  const cleanup = () => {
    video.removeEventListener('loadeddata', onFrame);
    video.removeEventListener('playing', onFrame);
    if (disconnectTimer != null) {
      window.clearTimeout(disconnectTimer);
      disconnectTimer = null;
    }
    if (connectTimer != null) {
      window.clearTimeout(connectTimer);
      connectTimer = null;
    }
    try {
      ws?.close();
    } catch {}
    try {
      pc?.close();
    } catch {}
    ws = null;
    pc = null;
  };

  // Report a lost connection exactly once (ws.onclose also fires after
  // our own cleanup, which used to schedule a second reconnect).
  const drop = () => {
    if (cancelled) return;
    cancelled = true;
    cleanup();
    onDisconnected();
  };

  pc = new RTCPeerConnection({
    iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
  });
  // A live peer that fails (or stays disconnected) is reconnected; a
  // short `disconnected` blip often recovers on its own.
  pc.onconnectionstatechange = () => {
    if (cancelled || !pc) return;
    const st = pc.connectionState;
    if (st === 'connected') {
      if (disconnectTimer != null) {
        window.clearTimeout(disconnectTimer);
        disconnectTimer = null;
      }
    } else if (st === 'failed' || st === 'closed') {
      drop();
    } else if (st === 'disconnected' && disconnectTimer == null) {
      disconnectTimer = window.setTimeout(() => {
        disconnectTimer = null;
        if (pc?.connectionState === 'disconnected') drop();
      }, WEBRTC_DISCONNECT_GRACE_MS);
    }
  };
  pc.onicecandidate = (ev) => {
    if (cancelled) return;
    const msg = webrtcCandidateMessage(ev.candidate?.candidate ?? '');
    if (offerSent) ws?.send(msg);
    else pendingLocal.push(msg);
  };
  pc.addTransceiver('video', { direction: 'recvonly' });
  pc.addTransceiver('audio', { direction: 'recvonly' });
  connectTimer = window.setTimeout(() => {
    connectTimer = null;
    if (!live) drop();
  }, WEBRTC_CONNECT_TIMEOUT_MS);
  pc.ontrack = (ev) => {
    if (cancelled) return;
    const [stream] = ev.streams;
    if (stream && video.srcObject !== stream) {
      // Clear any leftover MSE src= before swapping to MediaStream.
      try {
        video.removeAttribute('src');
        video.load();
      } catch {}
      video.srcObject = stream;
    }
  };

  const addRemoteCandidate = (candidate: string) => {
    // go2rtc bundles everything on the first m-line.
    pc?.addIceCandidate({ candidate, sdpMid: '0', sdpMLineIndex: 0 }).catch(() => {});
  };

  ws = new WebSocket(streamWsUrl(streamName, streamsBase));
  ws.binaryType = 'arraybuffer';
  ws.onopen = async () => {
    if (cancelled || !pc) return;
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      if (cancelled) return;
      ws?.send(webrtcOfferMessage(pc.localDescription?.sdp ?? offer.sdp ?? ''));
      offerSent = true;
      for (const m of pendingLocal.splice(0)) ws?.send(m);
    } catch (err) {
      console.error('webrtc offer failed', err);
      drop();
    }
  };
  ws.onmessage = async (ev) => {
    if (cancelled || !pc) return;
    const msg = parseWebrtcSignal(ev.data);
    if (!msg) return;
    if (msg.kind === 'error') {
      console.warn('[go2rtc/webrtc] server error', msg.message);
      drop();
    } else if (msg.kind === 'answer') {
      try {
        await pc.setRemoteDescription({ type: 'answer', sdp: msg.sdp });
        remoteSet = true;
        for (const c of pendingRemote.splice(0)) addRemoteCandidate(c);
      } catch (err) {
        console.error('webrtc answer failed', err);
        drop();
      }
    } else if (remoteSet) {
      addRemoteCandidate(msg.candidate);
    } else {
      pendingRemote.push(msg.candidate);
    }
  };
  ws.onerror = () => {
    // Browser logs the error; rely on onclose for the recovery branch.
  };
  ws.onclose = () => {
    // After the answer the media flows peer-to-peer; a closed signalling
    // socket only matters before the track arrived (connection state
    // covers a live peer).
    if (!live) drop();
  };

  return () => {
    cancelled = true;
    cleanup();
  };
}
